import {
  deltaTone,
  derivedDimensions,
  dimensionLabels,
  floorSeverity,
  formatCount,
  formatDelta,
  formatValue,
} from './model'

import styles from './ReportedMeetingsPage.module.scss'

import { type ReportDimension, type ReportGroup, severities } from '@/api/meetingReportsObservability'

export const SeverityMix = ({ counts, total }: { counts: ReportGroup['summary']['severity']; total: number }) => (
  <span
    className={styles.mix}
    role="img"
    aria-label={severities
      .filter((s) => counts[s] > 0)
      .map((s) => `${s.toUpperCase()} ${counts[s]}`)
      .join(', ')}
  >
    {severities.map((severity) =>
      counts[severity] ? (
        <span key={severity} className={styles[severity]} style={{ flex: counts[severity] / Math.max(total, 1) }} />
      ) : null
    )}
  </span>
)

const Board = ({
  dimension,
  groups,
  truncated,
  selected,
  onSelect,
}: {
  dimension: ReportDimension
  groups: ReportGroup[]
  truncated: boolean
  selected: string | undefined
  onSelect: (value: string) => void
}) => (
  <section className={styles.board} aria-label={`Reports grouped by ${dimensionLabels[dimension].toLowerCase()}`}>
    <div className={styles.boardHead}>
      <span>{dimensionLabels[dimension]}</span>
      <span>Worst</span>
      <span>Severity mix</span>
      <span className={styles.num}>Reports</span>
      <span className={styles.num}>Meetings</span>
      <span className={styles.num}>Δ prior</span>
    </div>
    {groups.length === 0 && <p className={styles.emptyRow}>No reports match these filters.</p>}
    {groups.map((group) => {
      const floor = floorSeverity(group.summary)
      const delta = formatDelta(group.summary.report_count, group.previous_report_count)
      return (
        <button
          key={group.value}
          type="button"
          className={`${styles.row} ${selected === group.value ? styles.selectedRow : ''}`}
          aria-pressed={selected === group.value}
          onClick={() => onSelect(group.value)}
        >
          <span className={styles.rowName}>
            <strong>{formatValue(dimension, group.value)}</strong>
            {dimension !== 'signature' && group.top_signature && (
              <small>
                top: {group.top_signature}
                {group.s0_signatures.length > 0 && (
                  <>
                    {' · '}
                    <span className={styles.s0Tag}>S0: {group.s0_signatures.join(', ')}</span>
                  </>
                )}
              </small>
            )}
            {dimension === 'signature' && (
              <small>
                {formatCount(group.summary.reporter_count)} reporter
                {group.summary.reporter_count === 1 ? '' : 's'}
              </small>
            )}
          </span>
          <span>{floor && <span className={`${styles.pill} ${styles[floor]}`}>{floor.toUpperCase()}</span>}</span>
          <SeverityMix counts={group.summary.severity} total={group.summary.report_count} />
          <span className={styles.num}>{formatCount(group.summary.report_count)}</span>
          <span className={styles.num}>{formatCount(group.summary.meeting_count)}</span>
          <span
            className={`${styles.num} ${styles[deltaTone(group.summary.report_count, group.previous_report_count)]}`}
          >
            {delta}
          </span>
        </button>
      )
    })}
    {(truncated || derivedDimensions.has(dimension)) && (
      <p className={styles.boardNote}>
        {truncated && 'Showing the 100 heaviest groups; narrow the filters to see the rest. '}
        {dimension === 'output_device_family' &&
          'Families are normalized from the raw output-device label; the Default-vs-Communications endpoint split is not persisted.'}
        {dimension === 'app_version' &&
          'Versions are recovered from the report text and may be missing on older reports.'}
      </p>
    )}
  </section>
)

export default Board
